/**
 * Bezier Path Utilities
 *
 * Math functions for calculating positions along SVG paths,
 * used for animating orbs along tree connector branches.
 */

interface Point {
  x: number;
  y: number;
}

/**
 * Get a point on a quadratic Bezier curve at parameter t (0-1)
 * Formula: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
 */
export function getPointOnQuadraticBezier(
  t: number,
  p0: Point,
  p1: Point,
  p2: Point
): Point {
  const oneMinusT = 1 - t;
  const oneMinusTSquared = oneMinusT * oneMinusT;
  const tSquared = t * t;

  return {
    x: oneMinusTSquared * p0.x + 2 * oneMinusT * t * p1.x + tSquared * p2.x,
    y: oneMinusTSquared * p0.y + 2 * oneMinusT * t * p1.y + tSquared * p2.y,
  };
}

/**
 * Get a point on a line segment at parameter t (0-1)
 */
export function getPointOnLine(t: number, start: Point, end: Point): Point {
  return {
    x: start.x + t * (end.x - start.x),
    y: start.y + t * (end.y - start.y),
  };
}

/**
 * Calculate the length of a line segment
 */
function lineLength(start: Point, end: Point): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Approximate the length of a quadratic Bezier curve using subdivision
 */
function bezierLength(p0: Point, p1: Point, p2: Point, segments = 20): number {
  let length = 0;
  let prevPoint = p0;

  for (let i = 1; i <= segments; i++) {
    const t = i / segments;
    const point = getPointOnQuadraticBezier(t, p0, p1, p2);
    length += lineLength(prevPoint, point);
    prevPoint = point;
  }

  return length;
}

/**
 * SVG Tree Connector Path Configuration
 *
 * Based on the existing implementation:
 * - Vertical rail: M 3 0 L 3 ${railEndY}
 * - Branch: M 3 ${centerY-12} Q 3 ${centerY}, 15 ${centerY} L 34 ${centerY}
 *
 * The curveRadius is 12, branchEndX is 34
 */
export const TREE_CONNECTOR = {
  railX: 3,           // X position of vertical rail
  curveRadius: 12,    // Radius of the curve
  branchEndX: 34,     // X position where branch meets card
  curveStartX: 15,    // X position where curve ends (3 + curveRadius)
} as const;

/**
 * Path segments for orb animation (from card toward progress bar)
 *
 * The orb travels in reverse: card → branch → curve → rail → top
 *
 * Segment breakdown for a branch at centerY:
 * 1. Horizontal line: (34, centerY) → (15, centerY) [19px]
 * 2. Quadratic curve: (15, centerY) → control(3, centerY) → (3, centerY-12) [~15px arc]
 * 3. Vertical rail: (3, centerY-12) → (3, 0) [centerY-12 px]
 */
export interface OrbPathConfig {
  cardCenterY: number;  // Y position of the sub-study card center
}

/**
 * Interpolate orb position along the complete path
 *
 * @param progress - Animation progress from 0 to 1
 * @param config - Path configuration
 * @returns Point {x, y} and opacity for the orb
 */
export function interpolateOrbPath(
  progress: number,
  config: OrbPathConfig
): Point & { opacity: number } {
  const { cardCenterY } = config;
  const { railX, curveRadius, branchEndX, curveStartX } = TREE_CONNECTOR;

  // Define the three path segments
  const horizontalStart: Point = { x: branchEndX, y: cardCenterY };
  const horizontalEnd: Point = { x: curveStartX, y: cardCenterY };

  const curveStart: Point = horizontalEnd;
  const curveControl: Point = { x: railX, y: cardCenterY };
  const curveEnd: Point = { x: railX, y: cardCenterY - curveRadius };

  const railStart: Point = curveEnd;
  const railEnd: Point = { x: railX, y: 0 };

  // Calculate segment lengths for proportional timing
  const horizontalLen = lineLength(horizontalStart, horizontalEnd); // 19px
  const curveLen = bezierLength(curveStart, curveControl, curveEnd); // ~15px
  const railLen = lineLength(railStart, railEnd); // cardCenterY - 12

  const totalLen = horizontalLen + curveLen + railLen;

  // Calculate segment boundaries as proportions of total length
  const horizontalProp = horizontalLen / totalLen;
  const curveProp = curveLen / totalLen;
  // railProp = 1 - horizontalProp - curveProp

  // Determine which segment we're in and local progress
  let point: Point;
  let opacity = 1;

  if (progress <= horizontalProp) {
    // Segment 1: Horizontal line
    const localT = progress / horizontalProp;
    point = getPointOnLine(localT, horizontalStart, horizontalEnd);
  } else if (progress <= horizontalProp + curveProp) {
    // Segment 2: Quadratic curve
    const localT = (progress - horizontalProp) / curveProp;
    point = getPointOnQuadraticBezier(localT, curveStart, curveControl, curveEnd);
  } else {
    // Segment 3: Vertical rail
    const localT = (progress - horizontalProp - curveProp) / (1 - horizontalProp - curveProp);
    point = getPointOnLine(localT, railStart, railEnd);

    // Fade out in the last 20% of rail travel
    const fadeStart = 0.8;
    if (localT > fadeStart) {
      opacity = 1 - (localT - fadeStart) / (1 - fadeStart);
    }
  }

  return { ...point, opacity };
}

/**
 * Calculate the total path length for a given card center Y
 * Useful for determining animation duration proportionally
 */
export function calculatePathLength(cardCenterY: number): number {
  const { railX, curveRadius, branchEndX, curveStartX } = TREE_CONNECTOR;

  const horizontalLen = branchEndX - curveStartX; // 19px
  const curveLen = bezierLength(
    { x: curveStartX, y: cardCenterY },
    { x: railX, y: cardCenterY },
    { x: railX, y: cardCenterY - curveRadius }
  );
  const railLen = cardCenterY - curveRadius;

  return horizontalLen + curveLen + railLen;
}
